// Copyright (c) 2019-2024 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit, Input, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { D3eService } from 'src/services/d3e-service';
import { IDeviceSetBare } from 'src/types/device-sets';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-threat-activity',
    templateUrl: './threat.activity.component.html',
    styleUrls: ['./threat.activity.component.scss', '../component.common.scss'],
    standalone: false
})

export class ThreatActivityComponent implements OnChanges {
  @Input() timeInterval: string;
  @Input() deviceSet: IDeviceSetBare;
  @Input() refreshTrigger: number;
  @Input() selectedOrgId: string;
  @Input() heightStyle = 260;

  componentHeight = '260px'
  graphStyle = '185px';

  view: any[] = [1900, 205];
  data = [];
  showLegend = true;
  showXAxis = true;
  showYAxis = true;

  showTotal = false;

  fThreatTitle: string;
  fThreatShow: string;
  fThreatInfo: string;
  constructor(
    private d3eService: D3eService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.translate.get(['DASH_TA_TITLE', 'DASH_TA_SHOW', 'DASH_TA_TIME']).subscribe(vals => {
      this.fThreatTitle = vals['DASH_TA_TITLE'];
      this.fThreatShow = vals['DASH_TA_SHOW'];
      this.fThreatInfo = vals['DASH_TA_TIME'];
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ( changes.deviceSet !== undefined ) {
      this.deviceSet = changes.deviceSet.currentValue;
    } 

    if ( changes.selectedOrgId !== undefined ) {
      this.selectedOrgId = changes.selectedOrgId.currentValue;
      if ( this.refreshTrigger == null ) {
        this.getThreatActivity();
      }
    }

    if ( changes.refreshTrigger !== undefined && changes.refreshTrigger.firstChange === false) {
      this.refreshTrigger = changes.refreshTrigger.currentValue;
    }

    if ( this.refreshTrigger ) {
       this.getThreatActivity();
    }

    if (changes.heightStyle !== undefined) {
      this.heightStyle = changes.heightStyle.currentValue;
      this.componentHeight = '' + this.heightStyle + 'px';
      this.graphStyle = '' + (this.heightStyle - 70) + 'px'
    }
  }

  getHeaderQueryParams() {
    const headerQueryParams = {};
    if ( this.timeInterval !== undefined && this.timeInterval != null ) {
      headerQueryParams['time'] = this.timeInterval;
    } else {
      headerQueryParams['time'] = '336';
    }
    if ( this.deviceSet !== undefined && this.deviceSet != null ) {
      headerQueryParams['deviceSetId'] = this.deviceSet.id.toString();
    }
    return headerQueryParams;
  }

  receiveAlertsChanged(e) {
    this.showTotal = e.checked;
    this.getThreatActivity();
  }

  getThreatActivity() {
    let deviceId = null;
    if ( this.deviceSet !== undefined && this.deviceSet != null) {
      deviceId = this.deviceSet.id;
    }

    this.data = []

    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    const queryJson = {
      'time': this.timeInterval,
      'deviceSet': deviceId,
      'n': 0,   // Not showing N, just show them all
      'totalEnabled': this.showTotal,
      'selectedOrgId': selectedOrg,
    };

    this.d3eService.get_activity(queryJson)
      .pipe(untilDestroyed(this))
      .subscribe(results => {
        const newSeriesList = [];
        
        Object.keys(results).forEach(function(seriesName) {
          const series = results[seriesName];
          const seriesList = [];
          const series_json = { 'name': seriesName, 
                                'series': seriesList };

          series.forEach(dataPoint => {
            const name = dataPoint['formatted_date'];
            const newDataPoint = {
              'name': name,
              'value': dataPoint['count'],
            };
            seriesList.push(newDataPoint);
          });
          newSeriesList.push(series_json);
        });
        
        this.data = newSeriesList;
        this.cdr.detectChanges();
    });
  }
}
